import { isDefined, Maybe, thenElse } from 'dg-web-shared/lib/MaybeV2.ts';
import * as PermitEditState from '../state/PermitEditState.ts';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import * as Flux from 'dg-web-shared/lib/Flux.tsx';
import { Updater } from 'dg-web-shared/lib/Flux.tsx';
import { PrimaryActionButton } from '../../ui/buttons/TextButton.tsx';
import * as Http from '../../api/Http.ts';
import { Formatter, Parser } from 'dg-web-shared/lib/Date.ts';

import { RefundContent } from './PermitCancellationSlideIn.tsx';
import * as SettingsState from '../../common/state/SettingsState.ts';
import { SlideInBody } from '../../ui/slidein/Slidein.tsx';
import { css } from '@emotion/css';
import { DatePicker } from '../../ui/date-picker/DatePicker.tsx';
import * as AsyncRequest from '../../AsyncRequest.ts';
import * as HttpResponse from 'dg-web-shared/lib/HttpResponse.ts';
import * as ResultState from '../../clearance-permit-list/state/ResultState.ts';
import * as FilteredPermitsState from '../../clearance-permit-list/state/FilteredPermitsState.ts';
import { LabeledText } from '../../ui/labeled-elements/LabeledText.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { Switch, SwitchType } from '../../ui/switches/Switches.tsx';
import { isFuture } from '../../clearance-permit-list/state/PermitsState.ts';
import { DateTime } from 'luxon';

const terminatePermit = AsyncRequest.request(
    Http.OperatorAccount.Permits.terminate,
    (store: Flux.Store, res: HttpResponse.Response): string => {
        PermitEditState.PermitCancellationResponse.setResponse(store, res);
        if (res.statusCode.cls.success) {
            PermitEditState.Terminate.reset(store);
            PermitEditState.Refund.reset(store);
            PermitEditState.Edit.reset(store);
            PermitEditState.Server.setResponse(store, res);
            ResultState.refetchSameContext(store, true);
            FilteredPermitsState.List.reset(store);
        }
        return 'PermitEdit-editPermit';
    },
);

export function TerminateHeaderButton({
    permit,
    terminate,
    refund,
    refData,
    isFormValid,
    update,
    activeColumn,
    setActiveColumn,
}: {
    permit: Maybe<PermitEditState.Permit>;
    terminate: PermitEditState.Terminate.State;
    refund: PermitEditState.Refund.State;
    refData: OperatorDataState.OperatorData | null;
    isFormValid: boolean;
    update: Updater;
    activeColumn: PermitTerminationActiveColumn;
    setActiveColumn: (activeColumn: PermitTerminationActiveColumn) => void;
}) {
    if (isDefined(permit) && isDefined(refData)) {
        return (
            <>
                {activeColumn === PermitTerminationActiveColumn.DatePicker ? (
                    <PrimaryActionButton
                        label={
                            <Localized
                                de="Weiter"
                                fr="Continuer"
                                it="Continua"
                                en="Continue"
                            />
                        }
                        onClick={() =>
                            setActiveColumn(
                                PermitTerminationActiveColumn.AmountEntry,
                            )
                        }
                        disabled={
                            !terminate.terminateInstantly &&
                            !isDefined(terminate.terminateDate)
                        }
                    />
                ) : (
                    <PrimaryActionButton
                        label={
                            <Localized
                                de="Beenden"
                                fr="Terminer"
                                it="Termina"
                                en="Terminate"
                            />
                        }
                        onClick={() => {
                            if (!isFormValid) {
                                update(store =>
                                    PermitEditState.Refund.stateWrite(store, {
                                        confirmPressed: true,
                                    }),
                                );
                            } else {
                                update(terminatePermit, {
                                    contractExternalId:
                                        permit.contractExternalId,
                                    terminate: {
                                        terminateDate: thenElse(
                                            terminate.terminateDate,
                                            to => Formatter.isoYearMonthDay(to),
                                            null,
                                        ),
                                        terminateInstantly:
                                            terminate.terminateInstantly,
                                        amount: refund.amount
                                            ? refund.amount
                                            : null,
                                        doRefund: refund.doRefund,
                                        remark:
                                            refund.remark ??
                                            (permit.remark || ''),
                                    },
                                });
                                setActiveColumn(
                                    PermitTerminationActiveColumn.DatePicker,
                                );
                            }
                        }}
                        error={refund.confirmPressed && !isFormValid}
                    />
                )}
            </>
        );
    } else {
        return null;
    }
}

export enum PermitTerminationActiveColumn {
    DatePicker = 0,
    AmountEntry,
}

export function TerminateBody({
    permit,
    confirmRefundAllowed,
    terminate,
    refund,
    settings,
    isFormValid,
    update,
    activeColumn,
}: {
    permit: Maybe<PermitEditState.Permit>;
    confirmRefundAllowed: boolean;
    terminate: PermitEditState.Terminate.State;
    refund: PermitEditState.Refund.State;
    settings: SettingsState.State;
    isFormValid: boolean;
    update: Updater;
    activeColumn: PermitTerminationActiveColumn;
}) {
    return (
        <SlideInBody>
            {activeColumn === PermitTerminationActiveColumn.DatePicker ? (
                <div
                    className={css({
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                    })}
                >
                    <Switch
                        label={
                            <Localized
                                de="Sofort"
                                fr="Immédiatement"
                                it="Subito"
                                en="Immediately"
                            />
                        }
                        onClick={() =>
                            update(store =>
                                PermitEditState.Terminate.stateWrite(store, {
                                    terminateInstantly: true,
                                }),
                            )
                        }
                        selected={terminate.terminateInstantly}
                        type={SwitchType.RadioButton}
                        disabled={
                            terminate.terminateInstantly ||
                            !permit ||
                            isFuture(permit)
                        }
                    />
                    <Switch
                        label={
                            <Localized
                                de="Später"
                                fr="Plus tard"
                                it="Più tardi"
                                en="Later"
                            />
                        }
                        onClick={() =>
                            update(store =>
                                PermitEditState.Terminate.stateWrite(store, {
                                    terminateInstantly: false,
                                }),
                            )
                        }
                        selected={!terminate.terminateInstantly}
                        type={SwitchType.RadioButton}
                        disabled={
                            !terminate.terminateInstantly ||
                            !permit ||
                            DateTime.fromISO(permit.validTo) <
                                DateTime.now().endOf('day')
                        }
                    />
                    {!terminate.terminateInstantly && (
                        <div
                            className={css({
                                display: 'flex',
                                justifyContent: 'center',
                            })}
                        >
                            <DatePicker
                                selectedDate={terminate.terminateDate}
                                currentDate={DateTime.now()}
                                onSelect={d =>
                                    update(store =>
                                        PermitEditState.Terminate.stateWrite(
                                            store,
                                            {
                                                terminateDate: d,
                                            },
                                        ),
                                    )
                                }
                                minDate={
                                    permit &&
                                    Parser.isoToDateTime(permit.validFrom) >
                                        DateTime.now()
                                        ? Parser.isoToDateTime(
                                              permit.validFrom,
                                          ).plus({ days: 1 })
                                        : DateTime.now().startOf('day')
                                }
                                maxDate={thenElse(
                                    permit,
                                    d =>
                                        Parser.isoToDateTime(d.validTo).minus({
                                            days: 1,
                                        }),
                                    DateTime.now(),
                                )}
                                language={settings.language}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <AmountEntry
                    permit={permit}
                    confirmRefundAllowed={confirmRefundAllowed}
                    terminate={terminate}
                    refund={refund}
                    settings={settings}
                    isFormValid={isFormValid}
                    update={update}
                />
            )}
        </SlideInBody>
    );
}

function AmountEntry({
    permit,
    confirmRefundAllowed,
    terminate,
    refund,
    settings,
    isFormValid,
    update,
}: {
    permit: Maybe<PermitEditState.Permit>;
    confirmRefundAllowed: boolean;
    terminate: PermitEditState.Terminate.State;
    refund: PermitEditState.Refund.State;
    settings: SettingsState.State;
    isFormValid: boolean;
    update: Updater;
}) {
    if (isDefined(permit)) {
        return (
            <div>
                <LabeledText
                    label={
                        <Localized
                            de="Bewilligung beenden per"
                            fr="Terminer l'autorisation le"
                            it="Terminare l'autorizzazione al"
                            en="Terminate permit by"
                        />
                    }
                >
                    {terminate.terminateInstantly ? (
                        <Localized
                            de="Sofort"
                            fr="Immédiatement"
                            it="Subito"
                            en="Immediately"
                        />
                    ) : (
                        thenElse(
                            terminate.terminateDate,
                            d => Formatter.dayMonthYear(d),
                            '',
                        )
                    )}
                </LabeledText>
                <RefundContent
                    permit={permit}
                    confirmRefundAllowed={confirmRefundAllowed}
                    onlyFullRefundAllowed={false}
                    refund={refund}
                    settings={settings}
                    isFormValid={isFormValid}
                    update={update}
                />
            </div>
        );
    } else {
        return null;
    }
}
